"use client";

import { OptimizedImage } from "@/components/ui/optimized-image";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import RealProductCard from "@/components/RealProductCard";
import RecommendationSections from "@/components/RecommendationSections";
import { cn } from "@/lib/utils";

import { LandingBlocksSection } from "./LandingBlocksSection";
import { RelatedRentalsNav } from "./RelatedRentalsNav";
import type { SearchLayoutProps } from "./types";

/**
 * Variant A: 반응형 레이아웃
 * - 데스크탑: 4열 그리드 (split view에서 카드가 컴팩트하게)
 * - 태블릿: 2~3열 기본 그리드
 * - 모바일: 풀폭 세로 카드 (캐치테이블 스타일)
 */
export default function SearchLayoutVariantA({
  products,
  locale,
  searchQuery,
  isMapAreaSearch,
  selectedCategories,
  region,
  onProductHover,
  t,
  size,
  unavailableMap,
  headerSlot,
  sortControl,
  relatedGuides,
  fallbackProducts,
  fallbackLocationLabel,
  fallbackTitle,
  totalCount,
  pagination,
  showTail = true,
  renderEmptyState,
  demandSlot,
  renderProducts,
  landingHeading,
  plainCount = false,
  relatedRentals,
  landingBlocks,
}: SearchLayoutProps) {
  // 건수는 현재 페이지가 아니라 **필터를 통과한 전체**를 센다.
  const resultCount = totalCount ?? products.length;
  // 정확한 수를 이미 알고 있으므로 "N개 이상"으로 뭉개지 않는다 — 지도에서 금액·개수를
  // 뺀 뒤로 수량을 말하는 곳이 여기 하나뿐이다(설계 D9).
  // 머리가 이미 주제를 말하면(색인 랜딩·카테고리 목록) "전체의 물품 N개" 대신 "물품 N개".
  const countText = isMapAreaSearch
    ? t("results.mapAreaCount", { count: resultCount })
    : plainCount
      ? t("drawer.items", { count: resultCount })
      : t("results.count", { location: searchQuery, count: resultCount });

  // 목록 맨 끝, 푸터 바로 위 "함께 찾는 렌탈"(B3). 모바일 드로어는 자체 인셋(px-4), 태블릿·
  // 데스크톱 목록 열은 이미 px-6 라 회색 띠를 열 끝까지 늘인다.
  const relatedNav = relatedRentals ? (
    <RelatedRentalsNav
      data={relatedRentals}
      className={size === "mobile" ? "px-4" : "-mx-6 px-6"}
    />
  ) : null;

  // 색인 랜딩 안내 블록 — 목록·가이드 아래, "함께 찾는 렌탈" 바로 위
  const landingSection = landingBlocks ? (
    <LandingBlocksSection
      data={landingBlocks}
      locale={locale}
      gutter={size === "mobile" ? "mobile" : "padded"}
    />
  ) : null;

  // 가이드 카드 1장. 모바일 카루셀에서만 폭·스냅 클래스가 덧붙는다.
  const renderGuideCard = (
    guide: NonNullable<typeof relatedGuides>[number],
    extraClassName?: string
  ) => (
    <Link
      key={guide.href}
      href={guide.href}
      className={cn(
        // 가로 스크롤 행이 상하 overflow를 잘라 바깥 링/그림자가 잘리므로
        // 포커스·액티브 표시는 안쪽 outline으로 (전역 outline-ring/50 색 상속).
        "group relative aspect-3/4 overflow-hidden rounded-xl bg-zinc-200",
        "focus-visible:outline-2 focus-visible:-outline-offset-2",
        "active:outline-2 active:-outline-offset-2",
        extraClassName
      )}
    >
      {guide.image && (
        <OptimizedImage
          src={guide.image}
          enablePlaceholder={false}
          alt=""
          fill
          sizes="(max-width: 640px) 50vw, 33vw"
          className="object-cover transition-transform duration-300 group-hover:scale-105"
        />
      )}
      {/* 하단 그라데이션 — 흰 제목의 대비 확보 (backdrop-blur 대비 저렴) */}
      <div className="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/75 via-black/30 to-transparent p-3 pt-8">
        <span
          className={cn(
            "line-clamp-2 font-medium text-white",
            // 모바일은 카드 폭이 41%뿐이라 14px 유지. 태블릿·데스크탑 그리드에서는
            // 이미지 대비 제목이 너무 작아 한 단계 올린다.
            size === "mobile" ? "text-sm" : "text-base"
          )}
        >
          {guide.title}
        </span>
      </div>
    </Link>
  );

  // 결과 유무와 무관하게 하단에 붙는 공통 섹션 — 세 렌더 브랜치에서 재사용한다.
  const guidesSection =
    relatedGuides && relatedGuides.length > 0 ? (
      <section
        className={cn(
          // mt-8: 위 그리드는 행 간 gap만 있어 마지막 행이 구분선에 붙는다.
          "mt-8 border-t border-zinc-100 pt-7",
          // 모바일 래퍼는 무패딩(상품 그리드가 자체 px-4를 씀) → 여기서 인셋을
          // 만든다. tablet/desktop 래퍼는 이미 p-6/px-6이라 덧대지 않는다.
          size === "mobile" && "px-4"
        )}
      >
        <div className="flex items-baseline justify-between gap-3">
          <h2
            className={cn(
              "font-semibold text-zinc-950",
              size === "mobile" ? "text-base" : "text-lg"
            )}
          >
            {t("results.relatedGuidesTitle")}
          </h2>
          <Link
            href={`/${locale}/blog`}
            className="flex shrink-0 items-center text-sm text-zinc-500 transition-colors hover:text-zinc-900"
          >
            {t("results.relatedGuidesViewAll")}
            <ChevronRight className="size-4" aria-hidden="true" />
          </Link>
        </div>
        {/* 고정 문구 — 검색어 보간은 쓰지 않는다. 서버(?q)와 클라이언트(?search)가
            읽는 파라미터가 달라 키워드가 "전체"로 새던 경로를 없앤 것. */}
        <p className="mt-1 text-sm leading-6 text-zinc-500">
          {t("results.relatedGuidesFallbackDescription")}
        </p>
        {size === "mobile" ? (
          // 바깥 스크롤 + 안쪽 flex 패딩(단일 컨테이너로 합치면 좌측 인셋이
          // 깨진다). -mx-4는 위 section의 px-4를 상쇄해 full-bleed 스크롤로.
          <div className="mt-4 -mx-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-px-4">
            <div className="flex gap-2.5 px-4">
              {relatedGuides.map((guide) =>
                renderGuideCard(guide, "w-[41%] shrink-0 snap-start")
              )}
              {/* overflow-x가 스크롤 끝에서 행의 오른쪽 패딩을 버린다
                  (카드가 행의 패딩 박스를 넘침) — 꼬리 스페이서로 끝 여백
                  복구: gap-2.5 10px + w-1.5 6px ≈ px-4 16px. */}
              <div className="w-1.5 shrink-0" aria-hidden />
            </div>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {relatedGuides.map((guide) => renderGuideCard(guide))}
          </div>
        )}
      </section>
    ) : null;

  if (products.length === 0) {
    return (
      <>
        {headerSlot}
        {/* 원인별 빈 화면(그림 + 제목 + 설명 + 버튼) — 좁은 모바일 드로어만 세로 정렬 */}
        {renderEmptyState?.(size !== "mobile")}
        {/* "필요해요" 수요 블록 — 빈 화면 안내 바로 아래, 추천 상품·가이드보다 위 */}
        {demandSlot && (
          <div
            className={cn(
              "pb-6",
              size === "mobile" ? "px-4" : "mx-auto w-full max-w-xl",
            )}
          >
            {demandSlot}
          </div>
        )}
        {/* 노출 여부는 서버가 결정 — 안 줄 상황이면 빈 배열이 온다. */}
        {fallbackProducts && fallbackProducts.length > 0 && (
          <section
            className={cn(
              "border-t border-zinc-100 pt-6",
              size === "mobile" && "px-4"
            )}
          >
            <h2
              className={cn(
                "font-semibold text-zinc-950",
                size === "mobile" ? "text-base" : "text-lg"
              )}
            >
              {fallbackTitle ??
                t("results.relatedProductsTitle", {
                  location: fallbackLocationLabel ?? "",
                })}
            </h2>
            <div
              className={cn(
                "mt-4 grid",
                size === "mobile"
                  ? "grid-cols-2 gap-x-3 gap-y-8"
                  : "grid-cols-2 gap-3 sm:grid-cols-3"
              )}
            >
              {fallbackProducts.map((product) => (
                <div
                  key={product.id}
                  onMouseEnter={() => onProductHover?.(product.id)}
                  onMouseLeave={() => onProductHover?.(null)}
                >
                  <RealProductCard
                    product={product}
                    isUnavailable={unavailableMap?.get(product.id)}
                  />
                </div>
              ))}
            </div>
          </section>
        )}
        {guidesSection}
        {relatedNav}
      </>
    );
  }

  // 모바일: 풀폭 세로 카드 리스트
  if (size === "mobile") {
    return (
      <>
        {/* 2열 그리드 카드 (카테고리 전용 목록이 있으면 그것) */}
        {renderProducts ? (
          renderProducts(products, size)
        ) : (
          <div className="grid grid-cols-2 gap-x-3 gap-y-8 px-4">
            {products.map((product) => (
              <div
                key={product.id}
                onMouseEnter={() => onProductHover?.(product.id)}
                onMouseLeave={() => onProductHover?.(null)}
              >
                <RealProductCard
                  product={product}
                  isUnavailable={unavailableMap?.get(product.id)}
                />
              </div>
            ))}
          </div>
        )}

        {pagination}

        {/* 추천·가이드 꼬리는 마지막 페이지에서만 — 페이지 사이에 끼면 목록이
            끝난 것처럼 보인다(설계 §5). */}
        {showTail && (
          <>
            {/* 추천 섹션 */}
            <div className="mt-6 px-4">
              <RecommendationSections
                products={products}
                locale={locale}
                categoryId={selectedCategories[0]}
                currentLocation={region}
                layout="vertical"
                showHotPlaces={false}
              />
            </div>

            {/* 가이드는 추천 섹션 래퍼 바깥에 — 자체 px-4와 카루셀 -mx-4를 쓰므로
                래퍼 안에 넣으면 이중 패딩으로 full-bleed 스크롤이 깨진다. */}
            {guidesSection}
          </>
        )}

        {landingSection}
        {relatedNav}
      </>
    );
  }

  // 태블릿/데스크탑: 그리드 레이아웃
  const gridClass =
    size === "tablet"
      ? "grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-6"
      : "grid grid-cols-4 gap-x-4 gap-y-6";

  const titleClass =
    size === "tablet"
      ? "text-lg font-bold text-gray-900"
      : "text-xl font-bold text-gray-900";

  return (
    <>
      {/* 헤더 슬롯 (필터바 등) */}
      {headerSlot}

      {/* 섹션 헤더 — 색인 랜딩이면 `<title>` 과 같은 문구. 페이지의 h1 은 SearchPageClient
          맨 위 sr-only 한 벌뿐이라(반응형 트리마다 h1 이 생기지 않게, 10-15) 여기는 aria-hidden p */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-1 h-5 bg-orange-500 rounded-full" />
          {landingHeading ? (
            <p aria-hidden="true" className={titleClass}>
              {landingHeading}
            </p>
          ) : (
            <h2 className={titleClass}>{searchQuery}</h2>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 ml-3">
          <p className="text-sm text-gray-500">{countText}</p>
          {sortControl}
        </div>
      </div>

      {/* 상품 그리드 (카테고리 전용 목록이 있으면 그것) */}
      {renderProducts ? (
        renderProducts(products, size)
      ) : (
        <div className={gridClass}>
          {products.map((product) => (
            <div
              key={product.id}
              onMouseEnter={() => onProductHover?.(product.id)}
              onMouseLeave={() => onProductHover?.(null)}
              className="transition-all duration-200"
            >
              <RealProductCard
                product={product}
                isUnavailable={unavailableMap?.get(product.id)}
              />
            </div>
          ))}
        </div>
      )}

      {pagination}

      {showTail && guidesSection}

      {landingSection}
      {relatedNav}
    </>
  );
}
